import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { LogOut, LayoutDashboard, Car, Settings as SettingsIcon, ClipboardList, Plus, Pencil, Eye, EyeOff, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { STATUSES, STATUS_COLORS, SEGMENTS, TRANSMISSIONS, FUELS, segmentLabel, formatDateTimeTR } from "@/lib/site";
import Logo from "@/components/Logo";

function Login({ login }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#171717] px-4">
      <div className="bg-white rounded-3xl p-8 sm:p-10 max-w-md w-full text-center shadow-2xl">
        <Logo className="h-12 mx-auto" />
        <h1 className="text-2xl font-bold text-[#171717] mt-6">Yönetim Paneli</h1>
        <p className="text-neutral-500 mt-2 text-sm">Devam etmek için yetkili hesabınızla giriş yapın.</p>
        <Button onClick={login} className="w-full h-12 mt-6 bg-[#A11016] hover:bg-[#820D12] text-white font-semibold" data-testid="admin-login-button">Google ile Giriş Yap</Button>
        <Link to="/" className="block mt-4 text-sm text-neutral-400 hover:text-[#A11016]">← Siteye Dön</Link>
      </div>
    </div>
  );
}

function Forbidden({ logout }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#171717] px-4">
      <div className="bg-white rounded-3xl p-10 max-w-md w-full text-center shadow-2xl">
        <ShieldAlert className="h-12 w-12 text-[#A11016] mx-auto" />
        <h1 className="text-xl font-bold text-[#171717] mt-4">Erişim Yetkiniz Yok</h1>
        <p className="text-neutral-500 mt-2 text-sm">Bu hesabın yönetim paneline erişim yetkisi bulunmuyor.</p>
        <Button onClick={logout} variant="outline" className="mt-6" data-testid="admin-logout-forbidden">Çıkış Yap</Button>
      </div>
    </div>
  );
}

const EMPTY_VEHICLE = { brand: "", model: "", name: "", segment: "ekonomik", transmission: "otomatik", fuel: "benzin", seats: 5, luggage: 1, ac: true, images: [], features: [], included: ["Kasko ve sigorta", "Sınırsız kilometre"], popular: false, active: true };

function VehicleForm({ open, onOpenChange, vehicle, onSaved }) {
  const [form, setForm] = useState(EMPTY_VEHICLE);
  const [saving, setSaving] = useState(false);
  const isEdit = !!vehicle?.id;

  useEffect(() => {
    if (open) setForm(vehicle ? { ...vehicle } : EMPTY_VEHICLE);
  }, [open, vehicle]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, name: form.name || `${form.brand} ${form.model}`, seats: Number(form.seats), luggage: Number(form.luggage) };
      if (isEdit) await api.put(`/admin/vehicles/${vehicle.id}`, payload);
      else await api.post("/admin/vehicles", payload);
      toast.success(isEdit ? "Araç güncellendi" : "Araç eklendi");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="vehicle-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Aracı Düzenle" : "Yeni Araç Ekle"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm mb-1 block">Marka</Label><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="vf-brand" /></div>
            <div><Label className="text-sm mb-1 block">Model</Label><Input value={form.model} onChange={(e) => set("model", e.target.value)} data-testid="vf-model" /></div>
          </div>
          <div><Label className="text-sm mb-1 block">Görünen Ad</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={`${form.brand} ${form.model}`} data-testid="vf-name" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-sm mb-1 block">Sınıf</Label>
              <Select value={form.segment} onValueChange={(v) => set("segment", v)}><SelectTrigger data-testid="vf-segment"><SelectValue /></SelectTrigger><SelectContent>{SEGMENTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-sm mb-1 block">Vites</Label>
              <Select value={form.transmission} onValueChange={(v) => set("transmission", v)}><SelectTrigger data-testid="vf-transmission"><SelectValue /></SelectTrigger><SelectContent>{TRANSMISSIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="text-sm mb-1 block">Yakıt</Label>
              <Select value={form.fuel} onValueChange={(v) => set("fuel", v)}><SelectTrigger data-testid="vf-fuel"><SelectValue /></SelectTrigger><SelectContent>{FUELS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-sm mb-1 block">Yolcu</Label><Input type="number" value={form.seats} onChange={(e) => set("seats", e.target.value)} data-testid="vf-seats" /></div>
            <div><Label className="text-sm mb-1 block">Bagaj</Label><Input type="number" value={form.luggage} onChange={(e) => set("luggage", e.target.value)} data-testid="vf-luggage" /></div>
          </div>
          <div><Label className="text-sm mb-1 block">Görsel URL'leri (virgülle ayırın)</Label>
            <Textarea rows={2} value={(form.images || []).join(", ")} onChange={(e) => set("images", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="vf-images" />
          </div>
          <div><Label className="text-sm mb-1 block">Donanım (virgülle ayırın)</Label>
            <Input value={(form.features || []).join(", ")} onChange={(e) => set("features", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="vf-features" />
          </div>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.popular} onChange={(e) => set("popular", e.target.checked)} data-testid="vf-popular" /> Popüler</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="vf-active" /> Aktif</label>
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={form.ac} onChange={(e) => set("ac", e.target.checked)} data-testid="vf-ac" /> Klima</label>
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#A11016] hover:bg-[#820D12] text-white" data-testid="vf-save">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user, loading, login, logout } = useAuth();
  const { settings, setSettings } = useSettings();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [vehicleForm, setVehicleForm] = useState({ open: false, vehicle: null });
  const [settingsForm, setSettingsForm] = useState(settings);

  const isAdmin = user?.is_admin;

  const loadAll = useCallback(async () => {
    try {
      const [s, b, v] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/vehicles", { params: { include_inactive: true } }),
      ]);
      setStats(s.data); setBookings(b.data); setVehicles(v.data);
    } catch {}
  }, []);

  useEffect(() => { document.title = "Yönetim Paneli | VoyaCar Rental"; }, []);
  useEffect(() => { if (isAdmin) loadAll(); }, [isAdmin, loadAll]);
  useEffect(() => { setSettingsForm(settings); }, [settings]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#171717]"><div className="h-10 w-10 border-4 border-[#A11016] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Login login={login} />;
  if (!isAdmin) return <Forbidden logout={logout} />;

  const filteredBookings = statusFilter === "all" ? bookings : bookings.filter((b) => b.status === statusFilter);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Durum güncellendi");
      const s = await api.get("/admin/stats"); setStats(s.data);
    } catch { toast.error("Güncellenemedi"); }
  };

  const toggleActive = async (v) => {
    try {
      await api.put(`/admin/vehicles/${v.id}`, { active: !v.active });
      loadAll();
      toast.success(v.active ? "Araç pasife alındı" : "Araç aktifleştirildi");
    } catch { toast.error("İşlem başarısız"); }
  };

  const saveSettings = async () => {
    try {
      const { _key, ...clean } = settingsForm;
      await api.put("/settings", clean);
      setSettings(clean);
      toast.success("Ayarlar kaydedildi");
    } catch { toast.error("Kaydedilemedi"); }
  };
  const setS = (k, v) => setSettingsForm((f) => ({ ...f, [k]: v }));

  const STAT_CARDS = stats ? [
    { label: "Toplam Talep", value: stats.total_bookings },
    { label: "Yeni Talep", value: stats.new_bookings },
    { label: "Onaylanan", value: stats.confirmed },
    { label: "Aktif Araç", value: stats.active_vehicles },
  ] : [];

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><Logo className="h-9" /><span className="hidden sm:inline text-sm font-medium text-neutral-400">Yönetim Paneli</span></div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-neutral-600">{user.name || user.email}</span>
            <Link to="/"><Button variant="outline" size="sm">Site</Button></Link>
            <Button variant="outline" size="sm" onClick={logout} className="gap-1 text-[#A11016] border-[#A11016]/30" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /> Çıkış</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {STAT_CARDS.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid={`stat-${c.label}`}>
              <div className="text-sm text-neutral-500">{c.label}</div>
              <div className="text-3xl font-bold text-[#171717] mt-1">{c.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="mb-6">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="gap-2"><ClipboardList className="h-4 w-4" /> Talepler</TabsTrigger>
            <TabsTrigger value="fleet" data-testid="tab-fleet" className="gap-2"><Car className="h-4 w-4" /> Filo</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="gap-2"><SettingsIcon className="h-4 w-4" /> Ayarlar</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <div className="flex items-center gap-3 mb-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px] bg-white" data-testid="booking-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">Tüm Durumlar</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-sm text-neutral-500">{filteredBookings.length} talep</span>
            </div>
            <div className="space-y-3">
              {filteredBookings.length === 0 && <div className="bg-white rounded-2xl border p-10 text-center text-neutral-500">Talep bulunmuyor.</div>}
              {filteredBookings.map((b) => (
                <div key={b.id} className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid={`booking-row-${b.code}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#A11016]">{b.code}</span>
                        <Badge className={`${STATUS_COLORS[b.status]} border-0 hover:opacity-100`}>{b.status}</Badge>
                      </div>
                      <div className="font-semibold text-[#171717]">{b.vehicle_name}</div>
                      <div className="text-sm text-neutral-500 mt-1">{b.full_name} • {b.phone} {b.email ? `• ${b.email}` : ""}</div>
                      <div className="text-sm text-neutral-500">{b.pickup_location} → {b.dropoff_location} • {formatDateTimeTR(b.pickup_datetime)} - {formatDateTimeTR(b.return_datetime)}</div>
                      <div className="text-sm text-neutral-500">{b.days} gün{b.flight_no ? ` • Uçuş: ${b.flight_no}` : ""}</div>
                      {b.extras?.length > 0 && <div className="text-xs text-neutral-400 mt-1">Ek: {b.extras.map((e) => e.label).join(", ")}</div>}
                      {b.note && <div className="text-xs text-neutral-400 mt-1 italic">Not: {b.note}</div>}
                    </div>
                    <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                      <SelectTrigger className="w-[180px]" data-testid={`booking-status-${b.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="fleet">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-neutral-500">{vehicles.length} araç</span>
              <Button onClick={() => setVehicleForm({ open: true, vehicle: null })} className="bg-[#A11016] hover:bg-[#820D12] text-white gap-2" data-testid="add-vehicle-button"><Plus className="h-4 w-4" /> Yeni Araç</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {vehicles.map((v) => (
                <div key={v.id} className={`bg-white rounded-2xl border p-4 ${v.active ? "border-neutral-200" : "border-neutral-200 opacity-60"}`} data-testid={`fleet-card-${v.slug}`}>
                  <div className="flex gap-3">
                    <img src={v.images?.[0]} alt={v.name} className="h-16 w-24 rounded-lg object-cover bg-neutral-100" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><span className="font-semibold text-[#171717] truncate">{v.name}</span>{!v.active && <Badge variant="outline" className="text-xs">Pasif</Badge>}</div>
                      <div className="text-xs text-neutral-500">{segmentLabel(v.segment)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => setVehicleForm({ open: true, vehicle: v })} data-testid={`edit-vehicle-${v.slug}`}><Pencil className="h-3.5 w-3.5" /> Düzenle</Button>
                    <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => toggleActive(v)} data-testid={`toggle-vehicle-${v.slug}`}>{v.active ? <><EyeOff className="h-3.5 w-3.5" /> Pasife Al</> : <><Eye className="h-3.5 w-3.5" /> Aktifleştir</>}</Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="settings">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 max-w-3xl space-y-5">
              <h3 className="font-bold text-[#171717]">İletişim Bilgileri</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-sm mb-1 block">Telefon (görünen)</Label><Input value={settingsForm.phone || ""} onChange={(e) => setS("phone", e.target.value)} data-testid="set-phone" /></div>
                <div><Label className="text-sm mb-1 block">Telefon (tel: linki)</Label><Input value={settingsForm.phone_link || ""} onChange={(e) => setS("phone_link", e.target.value)} data-testid="set-phone-link" /></div>
                <div><Label className="text-sm mb-1 block">WhatsApp No (905...)</Label><Input value={settingsForm.whatsapp || ""} onChange={(e) => setS("whatsapp", e.target.value)} data-testid="set-whatsapp" /></div>
                <div><Label className="text-sm mb-1 block">E-posta</Label><Input value={settingsForm.email || ""} onChange={(e) => setS("email", e.target.value)} data-testid="set-email" /></div>
                <div><Label className="text-sm mb-1 block">Adres</Label><Input value={settingsForm.address || ""} onChange={(e) => setS("address", e.target.value)} data-testid="set-address" /></div>
                <div><Label className="text-sm mb-1 block">Hizmet Bölgesi</Label><Input value={settingsForm.service_city || ""} onChange={(e) => setS("service_city", e.target.value)} data-testid="set-city" /></div>
                <div><Label className="text-sm mb-1 block">Çalışma Saatleri</Label><Input value={settingsForm.working_hours || ""} onChange={(e) => setS("working_hours", e.target.value)} data-testid="set-hours" /></div>
              </div>
              <h3 className="font-bold text-[#171717] pt-2">Sosyal Medya</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-sm mb-1 block">Instagram</Label><Input value={settingsForm.instagram || ""} onChange={(e) => setS("instagram", e.target.value)} data-testid="set-instagram" /></div>
                <div><Label className="text-sm mb-1 block">Facebook</Label><Input value={settingsForm.facebook || ""} onChange={(e) => setS("facebook", e.target.value)} data-testid="set-facebook" /></div>
              </div>
              <h3 className="font-bold text-[#171717] pt-2">Ana Sayfa Metinleri</h3>
              <div><Label className="text-sm mb-1 block">Hero Başlık</Label><Input value={settingsForm.hero_title || ""} onChange={(e) => setS("hero_title", e.target.value)} data-testid="set-hero-title" /></div>
              <div><Label className="text-sm mb-1 block">Hero Alt Metin</Label><Textarea rows={2} value={settingsForm.hero_subtitle || ""} onChange={(e) => setS("hero_subtitle", e.target.value)} data-testid="set-hero-subtitle" /></div>
              <div><Label className="text-sm mb-1 block">Kampanya Metni</Label><Input value={settingsForm.campaign_text || ""} onChange={(e) => setS("campaign_text", e.target.value)} data-testid="set-campaign" /></div>
              <h3 className="font-bold text-[#171717] pt-2">Analitik (isteğe bağlı)</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label className="text-sm mb-1 block">Google Analytics ID</Label><Input value={settingsForm.google_analytics_id || ""} onChange={(e) => setS("google_analytics_id", e.target.value)} data-testid="set-ga" /></div>
                <div><Label className="text-sm mb-1 block">Meta Pixel ID</Label><Input value={settingsForm.meta_pixel_id || ""} onChange={(e) => setS("meta_pixel_id", e.target.value)} data-testid="set-pixel" /></div>
              </div>
              <Button onClick={saveSettings} className="bg-[#A11016] hover:bg-[#820D12] text-white" data-testid="save-settings-button">Ayarları Kaydet</Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <VehicleForm open={vehicleForm.open} onOpenChange={(o) => setVehicleForm((f) => ({ ...f, open: o }))} vehicle={vehicleForm.vehicle} onSaved={loadAll} />
    </div>
  );
}
